import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, effect, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { PanelMenuModule } from 'primeng/panelmenu';
import { SidebarService } from '../../services/common/sidebar.service';
import { SidebarEnum } from '../../models/common/sidebar.enum';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { Sidebar, SidebarModule } from 'primeng/sidebar';
import { ButtonModule } from 'primeng/button';
import { UserModel } from '../../models/apis/system/user/user.model';
import { AuthService } from '../../services/common/auth.service';
import { SkeletonModule } from 'primeng/skeleton';
import { AclsConstants } from '../../constants/acls.constants';
import { routes } from '../../../app.routes';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrl: './sidebar.component.css',
    standalone: true,
    imports: [
        PanelMenuModule,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        FormsModule,
        CommonModule,
        RouterLink,
        MenuModule,
        TooltipModule,
        SidebarModule,
        ButtonModule,
        SkeletonModule
    ],
})
export class SidebarComponent implements OnInit {
    private readonly _sidebarService = inject(SidebarService);
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);
    private readonly _authService = inject(AuthService);

    currentUser = signal<UserModel | null>(null);
    currentAcls = signal<Array<string>>([]);


    menuItems: Array<any> = [
        // {
        //   label: 'Dashboard',
        //   icon: 'fa fa-tachometer',
        //   route: 'home',
        //   active: false
        // },
        {
            label: 'Meal Order Dashboard',
            icon: 'fa fa-user-md',
            route: '/patient/dashboard',
            acl: AclsConstants.NURSE_DASHBOARD.VIEW_NURSE_DASHBOARD
        },
        {
            label: 'Bulk Order',
            icon: 'fa fa-th',
            route: '/patient/bulk-orders',
            acl: AclsConstants.BULK_ORDER.VIEW_BULK_ORDER
        },
        {
            label: 'Pantry Requisition',
            icon: 'fa fa-list-alt',
            route: '/pantry-requisition',
            acl: AclsConstants.PANTRY_REQUISITION.VIEW_PANTRY_REQUISITION
        },
        // {
        //     label: 'Pantry Receiving',
        //     icon: 'fa fa-cart-plus',
        //     acl: AclsConstants.PANTRY_RECEIVING.VIEW_PANTRY_RECEIVING
        // },
        {
            label: 'Kitchen Dashboard',
            icon: 'fa fa-tachometer',
            route: '/patient/kitchen-dashboards',
            acl: AclsConstants.KITCHEN_DASHBOARD.VIEW_KITCHEN_DASHBOARD
        },
        {
            label: 'Event Calendar',
            icon: 'fa fa-calendar',
            route: '/catering/catering-event',
            acl: AclsConstants.EVENT_CALENDAR.VIEW_EVENT_CALENDAR
        },
        {
            label: 'Catering Orders',
            icon: 'fa fa-list',
            route: '/catering/catering-orders',
            acl: AclsConstants.CATERING_ORDER.VIEW_CATERING_ORDER
        },
        {
            label: 'Interface Log',
            icon: 'fa fa-exchange',
            acl: AclsConstants.INTERFACE_LOG.VIEW_INTERFACE_LOG,
            items: [
                { label: 'Queues', icon: 'fa fa-list', child: true, active: false, route: 'hl7/queues', acl: AclsConstants.INTERFACE_LOG.VIEW_INTERFACE_LOG },
                { label: 'Completed', icon: 'fa fa-list', child: true, active: false, route: 'hl7/completed-queues', acl: AclsConstants.INTERFACE_LOG.VIEW_INTERFACE_LOG },
                { label: 'Errors', icon: 'fa fa-list', child: true, active: false, route: 'hl7/error-queues', acl: AclsConstants.INTERFACE_LOG.VIEW_INTERFACE_LOG },
                { label: 'Mappings', icon: 'fa fa-cog', child: true, active: false, route: 'hl7/mappings', acl: AclsConstants.INTERFACE_LOG.VIEW_INTERFACE_LOG }

            ]
        },
        // {
        //     label: 'Cancel Discharge',
        //     icon: 'fa fa-undo',
        // },
        // {
        //     label: 'Meal Availability',
        //     icon: 'fa fa-pagelines',
        //     active: false
        // },
        // {
        //     label: 'Menu Simulator',
        //     icon: 'fa fa-pagelines',
        //     active: false
        // },
        { label: 'Master List of Inventory', icon: 'fa fa-cubes', route: 'inventory/inventories', acl: AclsConstants.INVENTORY.VIEW_INVENTORY },
        ...(environment.isNUHS ? [
            {
                label: 'Purchasing',
                icon: 'fa fa-shopping-bag',
                expanded: false,
                items: [
                    { label: 'Purchase Orders', icon: 'fa fa-truck', child: true, active: false, route: 'inventory/purchase-orders', acl: AclsConstants.PURCHASE_ORDER.VIEW_PURCHASE_ORDER },
                    { label: 'Goods Receivings', icon: 'fa fa-cart-plus', child: true, active: false, route: 'inventory/goods-receivings', acl: AclsConstants.GOODS_RECEIVING.VIEW_GOODS_RECEIVING },
                    { label: 'Goods Returns', icon: 'fa fa-undo', child: true, active: false, route: 'inventory/goods-returns', acl: AclsConstants.GOODS_RETURN.VIEW_GOODS_RETURN },
                    { label: 'Supplier Orders', icon: 'fa fa-file-text-o', child: true, active: false, route: 'inventory/supplier-orders' },
                ]
            },
            {
                label: 'Inventory Movement',
                icon: 'fa fa-book',
                expanded: false,
                items: [
                    { label: 'Inventory Requisitions', icon: 'fa fa-list', child: true, active: false, route: 'inventory/requisitions', acl: AclsConstants.REQUISITION.VIEW_REQUISITION },
                    { label: 'Stocks Returns', icon: 'fa fa-exchange', child: true, active: false, route: 'inventory/stocks-returns', acl: AclsConstants.STOCK_RETURN.VIEW_STOCK_RETURN },
                    { label: 'Stock Take', icon: 'fa fa-edit', child: true, active: false, route: 'inventory/stock-take', acl: AclsConstants.STOCK_TAKE.VIEW_STOCK_TAKE },
                ]
            }
        ] : [
            { label: 'Purchase Orders', icon: 'fa fa-truck', route: 'inventory/purchase-orders', acl: AclsConstants.PURCHASE_ORDER.VIEW_PURCHASE_ORDER },
            { label: 'Goods Receivings', icon: 'fa fa-cart-plus', route: 'inventory/goods-receivings', acl: AclsConstants.GOODS_RECEIVING.VIEW_GOODS_RECEIVING },
            { label: 'Goods Returns', icon: 'fa fa-undo', route: 'inventory/goods-returns', acl: AclsConstants.GOODS_RETURN.VIEW_GOODS_RETURN },
            { label: 'Inventory Requisitions', icon: 'fa fa-list', route: 'inventory/requisitions', acl: AclsConstants.REQUISITION.VIEW_REQUISITION },
            { label: 'Stocks Returns', icon: 'fa fa-exchange', route: 'inventory/stocks-returns', acl: AclsConstants.STOCK_RETURN.VIEW_STOCK_RETURN },
            { label: 'Stock Take', icon: 'fa fa-edit', route: 'inventory/stock-take', acl: AclsConstants.STOCK_TAKE.VIEW_STOCK_TAKE },
        ]),
        { label: 'Transaction Logs', icon: 'fa fa-clock-o', route: 'inventory/transaction-logs', acl: AclsConstants.INVENTORY_TRANSACTION_LOG.VIEW_INVENTORY_TRANSACTION_LOG },
        {
            label: 'Reports',
            icon: 'fa fa-list-alt',
            expanded: false,
            acl: AclsConstants.REPORTS.VIEW_REPORTS,
            items: [
                { label: 'Beverage Summary', icon: 'fa fa-glass', child: true, active: false, route: 'patient/beverage-summary-report', acl: AclsConstants['REPORTS_-_BEVERAGE_SUMMARY'].VIEW_BEVERAGE_SUMMARY_REPORT },
                { label: 'Birthday Cake', icon: 'fa fa-birthday-cake', child: true, active: false, route: 'patient/simple-birthday-cake-report', acl: AclsConstants['REPORTS_-_BIRTHDAY_CAKE']['VIEW_SIMPLE_BIRTHDAY_CAKE_REPORT'] },
                { label: 'Birthday Cake Order', icon: 'fa fa-birthday-cake', child: true, active: false, route: 'patient/birthday-cake-order-report', acl: AclsConstants['REPORTS_-_BIRTHDAY_CAKE']['VIEW_BIRTHDAY_CAKE_REPORT'] },
                { label: 'Bulk Meal Order', icon: 'fa fa-shopping-cart', child: true, active: false,route: 'patient/bulk-meal-order-reports', acl: AclsConstants['REPORTS_-_BULK_MEAL_ORDER'].VIEW_BULK_MEAL_ORDER_REPORT },
                { label: 'Catering Summary', icon: 'fa fa-cutlery', child: true, active: false, route: 'catering/catering-summary-report', acl: AclsConstants['REPORTS_-_CATERING_SUMMARY'].VIEW_CATERING_SUMMARY_REPORT},
                { label: 'Collation', icon: 'fa fa-cutlery', child: true, active: false, route: 'patient/collation-report', acl: AclsConstants['REPORTS_-_COLLATION'].VIEW_COLLATION_REPORT },
                { label: 'Collation of Meal Order summary', icon: 'fa fa-cutlery', child: true, active: false, route: 'patient/collation-meal-order-report', acl: AclsConstants['REPORTS_-_COLLATION_MEAL_ORDER'].VIEW_COLLATION_MEAL_ORDER_REPORT },
                { label: 'Confirmed Food Issue', icon: 'fa fa-cutlery', child: true, active: false, route: 'inventory/confirmed-food-issue-reports', acl: AclsConstants['REPORTS_-_CONFIRMED_FOOD_ISSUE'].VIEW_CONFIRMED_FOOD_ISSUE_REPORT },
                { label: 'Consolidated Feedback', icon: 'fa fa-comment-o', child: true, active: false, route: 'patient/consolidated-feedback-report', acl: AclsConstants['REPORTS_-_CONSOLIDATED_FEEDBACK_REPORT'].VIEW_CONSOLIDATED_FEEDBACK_REPORT },
                { label: 'Consolidated Meal Order', icon: 'fa fa-list-alt', child: true, active: false, route: 'patient/consolidated-meal-order-reports', acl: AclsConstants['REPORTS_-_CONSOLIDATED_MEAL_ORDER'].VIEW_CONSOLIDATED_MEAL_ORDER_REPORT },
                { label: 'Companion Meal Order Report', icon: 'fa fa-user-plus', child: true, active: false, route: 'patient/companion-meal-order-report', acl: AclsConstants['REPORTS_-_COMPANION_MEAL_ORDER_REPORT'].VIEW_GUEST_MEAL_ORDER_REPORT },
                { label: 'Diet Board', icon: 'fa fa-dashboard', child: true, active: false, route: 'patient/diet-board-report', acl: AclsConstants['REPORTS_-_DIET_BOARD'].VIEW_DIET_BOARD_REPORT },
                { label: 'Dish Feedback Report', icon: 'fa fa-cutlery', child: true, active: false, route: 'patient/dish-feedback-report', acl: AclsConstants['REPORTS_-_DISH_FEEDBACK'].VIEW_DISH_FEEDBACK_REPORT },
                { label: 'Extra Order Summary', icon: 'fa fa-plus-square', child: true, active: false,route: 'patient/extra-order-summary-reports', acl: AclsConstants['REPORTS_-_EXTRA_ORDER_SUMMARY'].VIEW_EXTRA_ORDER_SUMMARY_REPORT },
                { label: 'Extra Order Detail', icon: 'fa fa-file-text', child: true, active: false,route: 'patient/extra-order-detail-reports', acl: AclsConstants['REPORTS_-_EXTRA_ORDER_DETAIL'].VIEW_EXTRA_ORDER_DETAIL_REPORT },
                { label: 'Food Consumption', icon: 'fa fa-cutlery', child: true, active: false, route: 'inventory/food-consumption-reports', acl: AclsConstants['REPORTS_-_FOOD_CONSUMPTIONS'].VIEW_FOOD_CONSUMPTIONS_REPORT },
                { label: 'Food Costing', icon: 'fa fa-calculator', child: true, active: false, route: 'patient/food-costing-report', acl: AclsConstants['REPORTS_-_FOOD_COSTING'].VIEW_FOOD_COSTING_REPORT },
                { label: 'Free Text', icon: 'fa fa-comments', child: true, active: false, route: 'patient/free-text-report', acl: AclsConstants['REPORTS_-_FREE_TEXT'].VIEW_FREE_TEXT_REPORT },
                { label: 'Fruit & Desert', icon: 'fa fa-shopping-basket', child: true, active: false, route: 'patient/fruit-desert-report', acl: AclsConstants['REPORTS_-_FRUIT_DESERT'].VIEW_FRUIT_DESERT_REPORT },
                { label: 'Halal & Non-Halal Count', icon: 'fa fa-check-circle', child: true, active: false, route: 'patient/patient-count-report', acl: AclsConstants['REPORTS_-_HALAL_NON_HALAL_PATIENT_COUNT'].VIEW_HALAL_NON_HALAL_PATIENT_COUNT_REPORT },
                { label: 'Ingredient Transaction', icon: 'fa fa-book', child: true, active: false, route: 'inventory/ingredient-transaction-reports', acl: AclsConstants['REPORTS_-_INGREDIENT_TRANSACTION'].VIEW_INGREDIENT_TRANSACTION_REPORT },
                { label: 'Meal Consumption', icon: 'fa fa-cutlery', child: true, active: false, route: 'patient/meal-consumption-report', acl: AclsConstants['REPORTS_-_MEAL_CONSUMPTION'].VIEW_MEAL_CONSUMPTION_REPORT },
                { label: 'Meal Served Breakdown', icon: 'fa fa-pie-chart', child: true, active: false, route: 'patient/meal-served-breakdown-report', acl: AclsConstants['REPORTS_-_MEAL_SERVED_BREAKDOWN'].VIEW_MEAL_SERVED_BREAKDOWN_REPORT },
                { label: 'Meal Order Summary', icon: 'fa fa-file', child: true, active: false, route: 'patient/meal-order-summary-reports', acl: AclsConstants['REPORTS_-_MEAL_ORDER_SUMMARY'].VIEW_MEAL_ORDER_SUMMARY_REPORT },
                { label: 'Meal Order Amendment', icon: 'fa fa-history', child: true, active: false, route: 'patient/meal-order-amendment-reports', acl: AclsConstants['REPORTS_-_MEAL_ORDER_AMENDMENT'].VIEW_MEAL_ORDER_AMENDMENT_REPORT },
                { label: 'Non-Selection', icon: 'fa fa-ban', child: true, active: false, route: 'patient/non-selection-report', acl: AclsConstants['REPORTS_-_NON_SELECTION'].VIEW_NON_SELECTION_REPORT },
                { label: 'Pantry Requisition Summary', icon: 'fa fa-book', child: true, active: false, route: 'patient/pantry-requisition-summary-report', acl: AclsConstants['REPORTS_-_PANTRY_REQUISITION_SUMMARY'].VIEW_PANTRY_REQUISITION_SUMMARY_REPORT },
                { label: 'Pantry Requisition Detail', icon: 'fa fa-book', child: true, active: false, route: 'patient/pantry-requisition-detail-report', acl: AclsConstants['REPORTS_-_PANTRY_REQUISITION_DETAIL'].VIEW_PANTRY_REQUISITION_DETAILS_REPORT },
                { label: 'Patient Movement', icon: 'fa fa-exchange', child: true, active: false, route: 'patient/patient-movement-report', acl: AclsConstants['REPORTS_-_PATIENT_MOVEMENT'].VIEW_PATIENT_MOVEMENT_REPORT },
                { label: 'Plating Belt', icon: 'fa fa-circle-o-notch', child: true, active: false, route: 'patient/plating-belt-report', acl: AclsConstants['REPORTS_-_PLATING_BELT'].VIEW_PLATING_BELT_REPORT },
                { label: 'Postnatal Report', icon: 'fa fa-birthday-cake', child: true, active: false, route: 'patient/postnatal-report', acl: AclsConstants['REPORTS_-_POSTNATAL'].VIEW_POSTNATAL_REPORT },
                { label: 'Special Request', icon: 'fa fa-exclamation-circle', child: true, active: false, route: 'patient/special-request-report', acl: AclsConstants['REPORTS_-_SPECIAL_REQUEST'].VIEW_SPECIAL_REQUEST_REPORT },
                { label: 'Therapeutic Diet Population', icon: 'fa fa-heart', child: true, active: false,route: 'patient/therapeutic-diet-population-reports', acl: AclsConstants['REPORTS_-_THERAPEUTIC_DIET_POPULATION'].VIEW_THERAPEUTIC_DIET_POPULATION_REPORT },
                { label: 'Tea Break Forecast', icon: 'fa fa-coffee', child: true, active: false, route: 'patient/tea-break-forecast-report', acl: AclsConstants['REPORTS_-_TEA_BREAK_FORECAST'].VIEW_TEA_BREAK_FORECAST_REPORT },
                { label: 'Tea Snack', icon: 'fa fa-coffee', child: true, active: false, route: 'patient/tea-snack-report', acl: AclsConstants['REPORTS_-_TEA_SNACK'].VIEW_TEA_SNACK_REPORT },
                { label: 'User Access', icon: 'fa fa-user', child: true, active: false, route: 'patient/user-access-report', acl: AclsConstants['REPORTS_-_USER_ACCESS'].VIEW_USER_ACCESS_REPORT },
                { label: 'User Ordered', icon: 'fa fa-user', child: true, active: false, route: 'patient/user-ordered-report', acl: AclsConstants['REPORTS_-_USER_ORDERED'].VIEW_USER_ORDERED_REPORT }
                // { label: 'Feedback', icon: 'fa fa-comment', child: true, active: false,acl: AclsConstants['REPORTS_-_FEEDBACK'].VIEW_FEEDBACK_REPORT },
                // { label: 'Trial Meal Order', icon: 'fa fa-cutlery', child: true, active: false,acl: AclsConstants['REPORTS_-_TRIAL_MEAL_ORDER'].VIEW_TRIAL_MEAL_ORDER_REPORT },
                // { label: 'Meal Change & Cancelled', icon: 'fa fa-refresh', child: true, active: false,acl: AclsConstants['REPORTS_-_MEAL_CHANGE_ORDER_CANCELLED_SUMMARY'].VIEW_MEAL_CHANGE_ORDER_CANCELLED_SUMMARY_REPORT },
                // { label: 'Guest Meal Order Billing', icon: 'fa fa-user-circle-o', child: true, active: false },
                // { label: 'Special Condition', icon: 'fa fa-exclamation-circle', child: true, active: false,acl: AclsConstants['REPORTS_-_SPECIAL_CONDITION'].VIEW_SPECIAL_CONDITION_REPORT }
            ],
        },
        {
            label: 'Meal Management',
            icon: 'fa fa-cutlery',
            expanded: false,
            acl: AclsConstants.MEAL_ORDER_MANAGEMENT.VIEW_MEAL_ORDER_MANAGEMENT,
            items: [
                { label: 'Meal Classes', icon: 'fa fa-th-large', child: true, active: false, route: 'patient/meal-classes', acl: AclsConstants.MEAL_CLASS.VIEW_MEAL_CLASS },
                { label: 'Meal Periods', icon: 'fa fa-calendar-check-o', child: true, active: false, route: 'patient/meal-periods', acl: AclsConstants.MEAL_PERIOD.VIEW_MEAL_PERIOD },
                { label: 'Order Completeness', icon: 'fa fa-check-square-o', child: true, active: false, route: 'patient/order-completeness', acl: AclsConstants.ORDER_COMPLETENESS.VIEW_ORDER_COMPLETENESS },
                { label: 'Dish Types', icon: 'fa fa-tags', child: true, active: false, route: 'patient/dish-types', acl: AclsConstants.DISH_TYPE.VIEW_DISH_TYPE },
                { label: 'Dish Tags', icon: 'fa fa-tag', child: true, active: false, route: 'patient/dish-tags', acl: AclsConstants.DISH_TAG.VIEW_DISH_TAG },
                { label: 'Dishes', icon: 'fa fa-cutlery', child: true, active: false, route: 'patient/dishes', acl: AclsConstants.DISH.VIEW_DISH },
                { label: 'Menus', icon: 'fa fa-book', child: true, active: false, route: 'patient/menus', acl: AclsConstants.MENU.VIEW_MENU },
                { label: 'Menu Cycles', icon: 'fa fa-repeat', child: true, active: false, route: 'patient/menu-cycles', acl: AclsConstants.MENU_CYCLE.VIEW_MENU_CYCLE },
                { label: 'Cutoff Time', icon: 'fa fa-clock-o', child: true, active: false, route: 'patient/meal-order-cut-offs', acl: AclsConstants.CUTOFF_TIME.VIEW_CUTOFF_TIME },
                { label: 'Bulk Meal Categories', icon: 'fa fa-th-list', child: true, active: false, route: 'patient/bulk-meal-categories', acl: AclsConstants.BULK_MEAL_CATEGORY.VIEW_BULK_MEAL_CATEGORY },
                { label: 'Bulk Menu Cycles', icon: 'fa fa-arrows', child: true, active: false, route: 'patient/bulk-menu-cycles', acl: AclsConstants.BULK_MENU_CYCLE.VIEW_BULK_MENU_CYCLE },
                { label: environment.isNUHS ? 'Accommodation Code' : 'Treatment Categories', icon: 'fa fa-heartbeat', child: true, active: false, route: 'patient/treatment-categories', acl: AclsConstants.TREATMENT_CATEGORY.VIEW_TREATMENT_CATEGORY },
                { label: 'Restriction Types', icon: 'fa fa-ban', child: true, active: false, route: 'patient/restriction-types', acl: AclsConstants.RESTRICTION_TYPE.VIEW_RESTRICTION_TYPE },
                { label: 'Restrictions', icon: 'fa fa-exclamation-triangle', child: true, active: false, route: 'patient/restrictions', acl: AclsConstants.RESTRICTION.VIEW_RESTRICTION },
                { label: 'Extra Orders', icon: 'fa fa-plus-circle', child: true, active: false, route: 'patient/extra-orders', acl: AclsConstants.EXTRA_ORDER.VIEW_EXTRA_ORDER },
                { label: 'Kitchen Sections', icon: 'fa fa-building', child: true, active: false, route: 'patient/kitchen-sections', acl: AclsConstants.KITCHEN_SECTION.VIEW_KITCHEN_SECTION },
                { label: 'General Remarks', icon: 'fa fa-commenting', child: true, active: false, route: 'patient/general-remarks', acl: AclsConstants.GENERAL_REMARK.VIEW_GENERAL_REMARK },
                { label: 'Disciplines', icon: 'fa fa-tasks', child: true, active: false, route: 'patient/disciplines', acl: AclsConstants.DISCIPLINE.VIEW_DISCIPLINE },
                { label: 'Feedback Questionnaires', icon: 'fa fa-question-circle', child: true, active: false, route: 'patient/feedback-questionnaires', acl: AclsConstants.FEEDBACK_QUESTIONNAIRE.VIEW_FEEDBACK_QUESTIONNAIRE },
                { label: 'Dish Group', icon: 'fa fa-object-group', child: true, active: false, route: 'patient/dish-group', acl: AclsConstants.DISH_GROUP.VIEW_DISH_GROUP }
            ],
        },
        {
            label: 'Catering Management',
            icon: 'fa fa-truck',
            acl: AclsConstants.CATERING_MANAGEMENT.VIEW_CATERING_MANAGEMENT,
            expanded: false,
            items: [
                { label: 'Dish Types', icon: 'fa fa-tags', child: true, active: false, route: 'catering/catering-dish-types', acl: AclsConstants.CATERING_DISH_TYPE.VIEW_CATERING_DISH_TYPE },
                { label: 'Dish Tags', icon: 'fa fa-tag', child: true, active: false, route: 'catering/catering-dish-tags', acl: AclsConstants.CATERING_TAG.VIEW_CATERING_TAG },
                { label: 'Dishes', icon: 'fa fa-cutlery', child: true, active: false, route: 'catering/catering-dishes', acl: AclsConstants.CATERING_DISH.VIEW_CATERING_DISH },
                { label: 'Dish Sets', icon: 'fa fa-th-large', child: true, active: false, route: 'catering/catering-dish-sets', acl: AclsConstants.CATERING_DISH_SET.VIEW_CATERING_DISH_SET },
                { label: 'Menus', icon: 'fa fa-book', child: true, active: false, route: 'catering/catering-menus' },
                { label: 'Menu Cycles', icon: 'fa fa-repeat', child: true, active: false, route: 'catering/catering-menu-cycles', acl: AclsConstants.CATERING_MENU_CYCLE.VIEW_CATERING_MENU_CYCLE },
                { label: 'Kitchen Sections', icon: 'fa fa-building-o', child: true, active: false, route: 'catering/catering-kitchen-sections', acl: AclsConstants.CATERING_KITCHEN_SECTION.VIEW_CATERING_KITCHEN_SECTION },
                { label: 'Settings', icon: 'fa fa-cogs', child: true, active: false, route: 'catering/catering-settings' },
                { label: 'Internal Order', icon: 'fa fa-shopping-cart', child: true, active: false, route: 'catering/catering-internal-orders', acl: AclsConstants.WH_CATERING_INTERNAL_ORDER.VIEW_CATERING_INTERNAL_ORDER },
                { label: 'Payment Mode', icon: 'fa fa-dollar', child: true, active: false, route: 'catering/catering-payment-modes', acl: AclsConstants.CATERING_PAYMENT_MODE.VIEW_CATERING_PAYMENT_MODE },
                { label: 'Event Type', icon: 'fa fa-tags', child: true, active: false, route: 'catering/catering-event-types', acl: AclsConstants.CATERING_EVENT_TYPE.VIEW_CATERING_EVENT_TYPE },
            ],
        },
        // {
        //     label: 'Patient Management',
        //     icon: 'fa fa-wheelchair',
        //     expanded: false,
        //     active:false,
        //     items: [
        //       { label: 'Restriction Types', icon: 'fa fa-ban', child:true, active:false },
        //       { label: 'Restrictions', icon: 'fa fa-exclamation-circle', child:true, active:false },
        //       { label: 'Drug Allergies', icon: 'fa fa-medkit', child:true, active:false },
        //       { label: 'Precautions', icon: 'fa fa-warning', child:true, active:false },
        //       { label: 'Doctors', icon: 'fa fa-user-md', child:true, active:false },
        //     ],
        // },
        {
            label: 'Inventory Management',
            icon: 'fa fa-cubes',
            acl: AclsConstants.INVENTORY_MANAGEMENT.VIEW_INVENTORY_MANAGEMENT,
            expanded: false,
            items: [
                { label: 'Item Categories', icon: 'fa fa-bars', child: true, active: false, route: 'inventory/item-categories', acl: AclsConstants.INVENTORY_ITEM_CATEGORY.VIEW_INVENTORY_ITEM_CATEGORY },
                { label: 'Unit of Measures', icon: 'fa fa-tag', child: true, active: false, route: 'inventory/unit-of-measures', acl: AclsConstants.INVENTORY_UNIT_OF_MEASURE.VIEW_INVENTORY_UNIT_OF_MEASURE },
                { label: 'Items', icon: 'fa fa-cube', child: true, active: false, route: 'inventory/items', acl: AclsConstants.INVENTORY_ITEM.VIEW_INVENTORY_ITEM },
                { label: 'F&B Market Lists', icon: 'fa fa-book', child: true, active: false, route: 'patient/market-lists', acl: AclsConstants.MARKET_LIST.VIEW_MARKET_LIST },
                { label: 'Suppliers', icon: 'fa fa-truck', child: true, active: false, route: 'inventory/suppliers', acl: AclsConstants.INVENTORY_SUPPLIER.VIEW_INVENTORY_SUPPLIER },
                { label: 'PO Market Lists', icon: 'fa fa-book', child: true, active: false, route: 'inventory/market-lists', acl: AclsConstants.INVENTORY_MARKET_LIST.VIEW_INVENTORY_MARKET_LIST },
                { label: 'Settings', icon: 'fa fa-cogs', child: true, active: false, route: 'inventory/settings', acl: AclsConstants.INVENTORY_SETTINGS.VIEW_INVENTORY_SETTINGS },
            ],
        },
        {
            label: 'System Management',
            icon: 'fa fa-cog',
            acl: AclsConstants.SYSTEM_MANAGEMENT.VIEW_SYSTEM_MANAGEMENT,
            expanded: false,
            items: [
                { label: 'Hospitals', icon: 'fa fa-hospital-o', child: true, active: false, route: 'system/hospitals', acl: AclsConstants.HOSPITAL.VIEW_HOSPITAL },
                { label: 'Cost Centres', icon: 'fa fa-dollar', child: true, active: false, route: 'system/cost-centres', acl: AclsConstants.COST_CENTRE.VIEW_COST_CENTRE },
                { label: 'Departments', icon: 'fa fa-sitemap', child: true, active: false, route: 'system/departments', acl: AclsConstants.DEPARTMENT.VIEW_DEPARTMENT },
                { label: 'ACLs', icon: 'fa fa-lock', child: true, active: false, route: 'system/access-control-lists', acl: AclsConstants.ACL.VIEW_ACL },
                { label: 'ACL Groups', icon: 'fa fa-lock', child: true, active: false, route: 'system/access-control-list-groups', acl: AclsConstants.ACL_GROUP.VIEW_ACL_GROUP },
                { label: 'Roles', icon: 'fa fa-users', child: true, active: false, route: 'system/roles', acl: AclsConstants.USER_ROLE.VIEW_USER_ROLE },
                { label: 'Users', icon: 'fa fa-user', child: true, active: false, route: 'system/users', acl: AclsConstants.USER.VIEW_USER },
                { label: 'Announcements', icon: 'fa fa-bullhorn', child: true, active: false },
                { label: 'Audit Logs', icon: 'fa fa-file-text-o', child: true, active: false, route: 'system/audit-logs', acl: AclsConstants.AUDIT_LOG.VIEW_AUDIT_LOG },
                //   { label: 'Languages', icon: 'fa fa-language', child:true, active:false, route: 'system/languages' },
                //   { label: 'Translations', icon: 'fa fa-flag-o', child:true, active:false, route: 'system/translations'  },
                { label: 'Locations', icon: 'fa fa-map-marker', child: true, active: false, route: 'system/locations', acl: AclsConstants.LOCATION.VIEW_LOCATION },
                { label: 'Races', icon: 'fa fa-flag-checkered', child: true, active: false, route: 'system/races', acl: AclsConstants.RACE.VIEW_RACE },
                { label: 'Document Types', icon: 'fa fa-book', child: true, active: false, route: 'system/document-types', acl: AclsConstants.DOCUMENT_TYPE.VIEW_DOCUMENT_TYPE },
                { label: 'TV Dashboard Settings', icon: 'fa fa-television', child: true, active: false, route: 'patient/kitchen-tv-dashboard-settings', acl: AclsConstants.KITCHEN_TV_DASHBOARD_SETTINGS.VIEW_KITCHEN_TV_DASHBOARD_SETTINGS },
                { label: 'Plating Belt Settings', icon: 'fa fa-cogs', child: true, active: false, route: 'patient/plating-belt-settings', acl: AclsConstants.PLATING_BELT.VIEW_PLATING_BELT }
            ],
        },
        // {
        //     label: 'Samples',
        //     icon: 'fa fa-desktop',
        //     expanded: false,
        //     items: [
        //         { label: 'People', icon: 'fa fa-users', route: 'samples/people', child: true, active: false },
        //         { label: 'Claims', icon: 'fa fa-check-square-o', route: 'samples/claims', child: true, active: false }
        //     ],
        // },
    ];
    menuItemsTemp = signal<Array<any>>([]);
    filterMenu = signal<string>('');

    menuSkeletons = new Array(15);

    minWidth: number = 900;
    isMobileState: boolean = innerWidth < this.minWidth;
    sidebarState: SidebarEnum = this.isMobileState ? SidebarEnum.mobileCollapsed : SidebarEnum.desktopLarge;

    isMobileSidebarVisible: boolean = false;

    constructor() {
        effect(() => {
            let filter = this.filterMenu()?.trim();
            if (filter == '') {
                this.menuItemsTemp.set(this.menuItems);
            } else {
                    const lowerFilter = filter.toLowerCase();
                    this.menuItemsTemp.set(
                        this.menuItems
                            .map(menu => {
                                const items = menu.items?.filter((p: { label: string }) =>
                                    p.label?.toLowerCase().includes(lowerFilter)
                                );

                                const matchesMenu = menu.label?.toLowerCase().includes(lowerFilter);
                                const matchesItems = !!items?.length;

                                return matchesMenu || matchesItems
                                    ? { ...menu, items: matchesMenu ? menu.items : items }
                                    : null;
                            })
                            .filter((menu): menu is any => !!menu)
                    );
            }
        });
        this._authService.userAccountObservable.subscribe(auth => {
            if (auth) {
                this.currentUser.set(auth);

            }
        });

        this._authService.userRolesObservable.subscribe(async roles => {
            await this.performAclsCheck();
        });

        this._authService.userAclsObservable.subscribe(async acls => {
            if (acls) {
                this.currentAcls.set(acls);
            }
        });
        this._sidebarService.sidebarStateObservable.subscribe({
            next: (state) => {
                this.sidebarState = state;
                this.isMobileState = state == SidebarEnum.mobileCollapsed || state == SidebarEnum.mobileExpanded;
                if (state == SidebarEnum.mobileExpanded) {
                    this.isMobileSidebarVisible = true;
                }
                this._changeDetectorRef.detectChanges();
            }
        });
        this._sidebarService.isMobileResolutionObservable.subscribe({
            next: (isMobileState) => {
                this.isMobileState = isMobileState;
            }
        });
    }

    async ngOnInit() {
        await this.performAclsCheck();
    }

    async performAclsCheck() {
        const temp = this.menuItemsTemp();
        if (!temp?.length) return;

        await Promise.all(
            temp.map(async (menu) => {
                if (!menu?.acl) {
                    if (menu.items?.length) {
                        await Promise.all(
                            menu.items.map(async (submenu: any) => {
                                if (!submenu?.acl) return;
                                submenu.visible = await this._authService.isAuthorized(<string>submenu.acl);
                            })
                        );
                        menu.visible = menu.items.some((submenu: any) => submenu.visible === true);
                    } else {
                        menu.visible = true;
                    }
                    return;
                }

                const authorized = await this._authService.isAuthorized(<string>menu.acl);
                menu.visible = authorized;

                if (authorized && menu.items?.length) {
                    await Promise.all(
                        menu.items.map(async (submenu: any) => {
                            if (!submenu?.acl) return;
                            submenu.visible = await this._authService.isAuthorized(<string>submenu.acl);
                        })
                    );
                }
            })
        );

        this._changeDetectorRef.detectChanges();
    }

    onCloseMobileSidebar(sidebarRef: Sidebar, event: any) {
        sidebarRef.close(event);
        this.sidebarState = SidebarEnum.mobileCollapsed;
        this._sidebarService.setState(this.sidebarState);
        this._changeDetectorRef.detectChanges();

    }
}
